import React, { useEffect } from 'react';
import Dialog from "../../components/controls/Dialog";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import * as skillService from '../../services/skillService';
import * as projectService from '../../services/projectService';
import * as watsonService from '../../services/watsonService';
import Input from '../../components/controls/Input';
import Select from '../../components/controls/Select';
import { useForm } from '../../components/useForm';
import useAlert from '../../components/useAlert';
import * as PREDEFINED_VALUES from '../../const/consts';
import { FormControl, InputLabel, MenuItem, Select as MuiSelect } from '@mui/material';

const initialFValues = {
    id: '',
    idDeprecated: 0,
    name: '',
    projectId: '',
    language: PREDEFINED_VALUES.languages[0],
    watsonWorkspaceId: '',
    watsonApiKey: '',
    watsonUrl: '',
    watsonVersion: '',
    watsonIdDeprecated: 0
}

const SkillDialog = (props) => {
    const { open, selectedRow, getSkills } = props;
    const [projects, setProjects] = React.useState([]);
    const [watsons, setWatsons] = React.useState([]);

    const {
        values,
        setValues,
        errors,
        handleInputChange,
        handleClose,
        handleError
    } = useForm(initialFValues, props);

    useEffect(() => {
        projectService.getProjects().then(response => {
            const items = response.data.items || [];
            setProjects(items);
            if (!selectedRow && items.length > 0) {
                setValues((prevValues) => ({
                    ...prevValues,
                    projectId: prevValues.projectId || items[0].id
                }));
            }
        });
    }, [selectedRow, setValues]);

    useEffect(() => {
        watsonService.getAll().then(response => {
            const items = response.data.items || [];
            setWatsons(items);
            if (!selectedRow && items.length > 0) {
                setValues((prevValues) => ({
                    ...prevValues,
                    watsonIdDeprecated: prevValues.watsonIdDeprecated || items[0].id
                }));
            }
        });
    }, [selectedRow, setValues]);

    useEffect(() => {
        if (open)
            handleAlertClose();
        if (selectedRow)
            setValues({
                ...selectedRow,
                idDeprecated: selectedRow.idDeprecated
                    ?? 0,
                watsonIdDeprecated: selectedRow.watsonIdDeprecated
                    ?? 0
            });
        else
            setValues((prevValues) => ({
                ...initialFValues,
                projectId: prevValues.projectId || (projects[0]?.id || '')
            }));
        //eslint-disable-next-line
    }, [selectedRow, open, projects, setValues])

    const {
        Alert,
        status,
        showSuccess,
        showError,
        handleAlertClose
    } = useAlert();

    const submit = () => {
        if (validate()) {
            const payload = {
                ...values,
                idDeprecated: values.idDeprecated === '' ? 0 : parseInt(values.idDeprecated, 10),
                watsonIdDeprecated: values.watsonIdDeprecated === '' ? 0 : parseInt(values.watsonIdDeprecated, 10)
            };
            if (selectedRow)
                skillService.updateSkill(selectedRow.id, payload)
                    .then(() => {
                        showSuccess();
                        handleClose();
                        getSkills();
                    })
                    .catch(error => {
                        showError(error.response?.data?.message || "Error updating Skill");
                        handleError(error.response?.data?.fields);
                    });
            else
                skillService.createSkill(payload)
                    .then(() => {
                        showSuccess();
                        handleClose();
                        getSkills();
                    })
                    .catch(error => {
                        showError(error.response?.data?.message || "Error creating Skill");
                        handleError(error.response?.data?.fields);
                    });
        }
    };

    const validate = () => {
        let temp = {}
        temp.id = values.id ? "" : "ID is required";
        temp.name = values.name ? "" : "Name is required";
        temp.projectId = values.projectId ? "" : "Project ID is required";
        return Object.values(temp).every(x => x === '');
    }

    return (
        <React.Fragment>
            {status ? <Alert /> : null}
            <Dialog
                title={selectedRow ? "Edit Skill" : "Create new Skill"}
                open={open}
                handleClose={handleClose}>
                <DialogContent>
                    <Input
                        {...(selectedRow && { disabled: true })}
                        label="ID"
                        name="id"
                        defValue={values.id}
                        onChange={handleInputChange}
                        error={errors.id}
                    />
                    <Input
                        label="Name"
                        name="name"
                        defValue={values.name}
                        onChange={handleInputChange}
                        error={errors.name}
                    />
                    <Input
                        label="ID (deprecated)"
                        name="idDeprecated"
                        type="number"
                        defValue={values.idDeprecated}
                        onChange={handleInputChange}
                        error={errors.idDeprecated}
                    />
                    <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
                        <InputLabel>Project ID</InputLabel>
                        <MuiSelect
                            label="Project ID"
                            name="projectId"
                            value={projects.some(p => p.id === values.projectId) ? values.projectId : (projects[0]?.id || '')}
                            onChange={handleInputChange}
                            error={!!errors.projectId}
                        >
                            {projects.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name} ({p.id})</MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>
                    <Select
                        label="Language"
                        name="language"
                        defValue={values.language}
                        onChange={handleInputChange}
                        options={PREDEFINED_VALUES.languages}
                    />
                    <Input
                        label="Watson Workspace ID"
                        name="watsonWorkspaceId"
                        defValue={values.watsonWorkspaceId}
                        onChange={handleInputChange}
                        error={errors.watsonWorkspaceId}
                    />
                    <Input
                        label="Watson API Key"
                        name="watsonApiKey"
                        defValue={values.watsonApiKey}
                        onChange={handleInputChange}
                        error={errors.watsonApiKey}
                    />
                    <Input
                        label="Watson URL"
                        name="watsonUrl"
                        defValue={values.watsonUrl}
                        onChange={handleInputChange}
                        error={errors.watsonUrl}
                    />
                    <Input
                        label="Watson Version"
                        name="watsonVersion"
                        defValue={values.watsonVersion}
                        onChange={handleInputChange}
                        error={errors.watsonVersion}
                    />
                    <FormControl fullWidth size="small" sx={{ marginTop: 2 }} disabled={watsons.length === 0}>
                        <InputLabel>Watson ID (deprecated)</InputLabel>
                        <MuiSelect
                            label="Watson ID (deprecated)"
                            name="watsonIdDeprecated"
                            value={watsons.some(w => w.id === values.watsonIdDeprecated)
                                ? values.watsonIdDeprecated
                                : (watsons[0]?.id || '')}
                            onChange={handleInputChange}
                            error={!!errors.watsonIdDeprecated}
                        >
                            {watsons.map(w => (
                                <MenuItem key={w.id} value={w.id}>{w.name} ({w.id})</MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={submit}>Save</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default SkillDialog;
